import type { RoundAction } from "../../contracts/RoundSnapshot";
import type { ReplayContext } from "../core/ReplayContext";

/**
 * Action handlers are atomic instruction handlers: one action type each, payload validated, then
 * the presentation it drives (a presenter callback the handler was constructed with).
 */
export interface RoundActionHandler {
  readonly actionTypeName: string;

  handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void>;
}

const NO_HANDLERS: readonly RoundActionHandler[] = [];

/**
 * Several handlers may register for one action type; the engine runs them sequentially in
 * registration order (ordered fan-out, plan §2.7). Registration order is the plugin-list order.
 */
export class ActionHandlerRegistry {
  private readonly handlersByTypeName: Map<string, RoundActionHandler[]> = new Map();

  public registerHandler(handler: RoundActionHandler): void {
    const list = this.handlersByTypeName.get(handler.actionTypeName);
    if (list) list.push(handler);
    else this.handlersByTypeName.set(handler.actionTypeName, [handler]);
  }

  public getHandlers(actionTypeName: string): readonly RoundActionHandler[] {
    return this.handlersByTypeName.get(actionTypeName) ?? NO_HANDLERS;
  }
}